import { RefObject, useEffect, useMemo, useState } from 'react'
import EmptyLink from '@aytm/app/swidget/base/components/EmptyLink'
import useSurveyLocale from '@aytm/app/swidget/base/modules/locale/useSurveyLocale'
import useViewport from '@aytm/app/swidget/base/modules/viewport/useViewport.ts'
import { unescapeTags } from '@aytm/lib/common/func'
import { getBigAssetPath, getMediumAssetPath, getSmallAssetPath } from '@aytm/lib/common/assets'
import { IImageAsset } from '@aytm/app/swidget/base/modules/images/types'
import {
  ASSETS_TYPES,
  EcommerceBannerPlacement,
  EcommerceResponse,
  IEcommerceVideoAsset,
  IEcommerceAnswerSchema,
  IEcommerceQuestionSchema
} from '../../types.ts'
import { formatPriceWithLocale } from '../../utils/formatPriceWithLocale.ts'
import { defaultAssetIndex } from '../../utils/defaultAssetIndex.ts'
import { getAssetType } from '../../utils/getAssetType.ts'
import useAssetsCollectionBuilder from '../../hooks/useAssetsCollectionBuilder.ts'
import AssetsGate from '../../components/AssetsGate.tsx'
import Spinner from './Spinner.tsx'
import AddToCartButton from './AddToCartButton.tsx'
import AldiPriceMatch from './AldiPriceMatch.tsx'
import ClubcardPrice from './ClubcardPrice.tsx'
import Gallery from './Gallery.tsx'
import Rating from './Rating.tsx'
import FeaturedCarousel from './FeaturedCarousel.tsx'
import VariantSelect from './VariantSelect.tsx'
import style from './Product.module.scss'
import favouriteIcon from '../assets/icons/favourite.svg'

interface IProductProps {
  schema: IEcommerceQuestionSchema
  answer: IEcommerceAnswerSchema
  response: EcommerceResponse
  onItemClose: () => void
  onItemSelect: (index: number, bannerPlacement?: EcommerceBannerPlacement) => void
  onAddToCart: (index: number, qnt: number, inCarousel?: boolean) => void
  scrollTop: () => void
  scrollRootRef: RefObject<HTMLDivElement>
  onTitleHiddenChange: (hidden: boolean) => void
}

// The app bar's height (layout.scss $tesco-mobile-app-bar-height): what slides under it counts as scrolled away.
const MOBILE_APP_BAR_HEIGHT = 64

// True once the element has scrolled up under the app bar; false while it is visible or still below.
const useScrolledPast = (element: HTMLElement | null, rootRef: RefObject<HTMLDivElement>) => {
  const [past, setPast] = useState<boolean>(false)

  useEffect(() => {
    const root = rootRef.current
    if (!element || !root) return
    const observer = new IntersectionObserver(
      ([entry]) => setPast(!entry.isIntersecting && entry.boundingClientRect.top < (entry.rootBounds?.top ?? 0)),
      { root, rootMargin: `-${MOBILE_APP_BAR_HEIGHT}px 0px 0px 0px` }
    )
    observer.observe(element)
    return () => observer.disconnect()
  }, [element, rootRef])

  return past
}

// Accordions the page draws but never opens: there is no data behind them in the survey.
const DECORATIVE_SECTIONS = [
  'sectionIngredients',
  'sectionStorage',
  'sectionMoreInformation',
  'sectionBrandDetails',
  'sectionSafety'
]

const LEGAL_PARAGRAPHS = ['legalParagraph1', 'legalParagraph2', 'legalParagraph3', 'legalParagraph4']

const Product = ({
  schema,
  answer,
  response,
  onItemClose,
  onItemSelect,
  onAddToCart,
  scrollTop,
  scrollRootRef,
  onTitleHiddenChange
}: IProductProps) => {
  const { t, languageTag } = useSurveyLocale()
  const viewport = useViewport()
  const [aboutOpen, setAboutOpen] = useState<boolean>(true)
  // Callback refs: the page mounts behind the assets gate, so the observers start when these appear.
  const [titleElement, setTitleElement] = useState<HTMLElement | null>(null)
  const [buyRowElement, setBuyRowElement] = useState<HTMLElement | null>(null)
  const titlePast = useScrolledPast(titleElement, scrollRootRef)
  const buyRowPast = useScrolledPast(buyRowElement, scrollRootRef)

  useEffect(() => onTitleHiddenChange(titlePast), [titlePast, onTitleHiddenChange])
  // Leaving the page hands the app bar back without a title.
  useEffect(() => () => onTitleHiddenChange(false), [onTitleHiddenChange])
  const price = (value: number) => formatPriceWithLocale(value, schema.price_mask, languageTag, true, false)
  // A rating below one star is unreachable for a real product, so it is treated as no rating.
  const hasRating = Number(answer.rating) >= 1
  const hasAbout = Boolean(answer.description || answer.feature_bullets)

  // "Usually bought next" = featured top-level products, excluding the open one.
  const featured = useMemo(
    () => schema.answers.filter(item => item.featured && !item.na && !item.parent_product && item.id !== answer.id),
    [schema.answers, answer.id]
  )

  useEffect(() => scrollTop(), [scrollTop])

  // The page waits for what the gallery and the carousel draw first: the main asset, the strip of
  // thumbnails and the carousel cards. A model loads inside its own viewer.
  const assetsCollection = useAssetsCollectionBuilder(answer)
  const mainAsset = assetsCollection[defaultAssetIndex(assetsCollection)]
  const pageImages = [
    mainAsset && ASSETS_TYPES.IMAGE === getAssetType(mainAsset) && getBigAssetPath(mainAsset as IImageAsset),
    ...assetsCollection.map(asset => {
      const type = getAssetType(asset)
      if (ASSETS_TYPES.IMAGE === type) return getSmallAssetPath(asset as IImageAsset)
      if (ASSETS_TYPES.VIDEO === type) return (asset as IEcommerceVideoAsset).video.thumbnail
      return null
    }),
    ...featured.map(item => item.image && getMediumAssetPath(item.image))
  ]

  // Another product of the page (a variant, a carousel card) replaces this one: close, then open.
  const openProduct = (index: number, bannerPlacement?: EcommerceBannerPlacement) => {
    onItemClose()
    onItemSelect(index, bannerPlacement)
  }

  const breadcrumbs = (
    <nav className={style.breadcrumbs}>
      <button
        type="button"
        className={style.crumbHome}
        onClick={onItemClose}
        aria-label={t('questions', 'ec', 'tesco', 'home')}
      >
        <i className="pi pi-home" />
      </button>
      <i className={`pi pi-angle-right ${style.crumbSeparator}`} />
      {schema.search_condition && (
        <>
          <button
            type="button"
            className={style.crumbLink}
            onClick={onItemClose}
            dangerouslySetInnerHTML={{ __html: schema.search_condition }}
          />
          <i className={`pi pi-angle-right ${style.crumbSeparator}`} />
        </>
      )}
      <span className={style.crumbCurrent} dangerouslySetInnerHTML={{ __html: answer.text }} />
    </nav>
  )

  const info = (
    <div className={style.info}>
      {answer.sponsored && <div className={style.sponsored}>{t('questions', 'ec', 'tesco', 'sponsored')}</div>}
      <h1 className={style.title} dangerouslySetInnerHTML={{ __html: answer.text }} />
      {hasRating && (
        <div className={style.ratingRow}>
          <Rating rating={answer.rating} />
          <span className={style.ratingText}>
            {t<(rating: string) => string>('questions', 'ec', 'tesco', 'stars')(Number(answer.rating).toFixed(1))}
            {answer.reviews != null && (
              <>
                {' ('}
                <EmptyLink color="#00539f">
                  <span className={style.reviewsLink}>
                    {t<(count: string) => string>(
                      'questions',
                      'ec',
                      'tesco',
                      'reviews'
                    )(answer.reviews.toLocaleString(languageTag))}
                  </span>
                </EmptyLink>
                {')'}
              </>
            )}
          </span>
        </div>
      )}

      <div className={style.priceRow}>
        <span className={style.price} dangerouslySetInnerHTML={{ __html: price(answer.price) }} />
        {answer.per_count_price && (
          <span
            className={style.unitPrice}
            dangerouslySetInnerHTML={{ __html: unescapeTags(answer.per_count_price) }}
          />
        )}
      </div>

      <AddToCartButton
        amount={response.answers[answer.index].v}
        limit={schema.max_qty_per_product}
        withInput
        onAdd={qnt => onAddToCart(answer.index, qnt)}
      />

      <VariantSelect schema={schema} answer={answer} onSelect={index => openProduct(index)} />
      <ClubcardPrice schema={schema} answer={answer} />
      <AldiPriceMatch answer={answer} />

      {answer.best_seller && (
        <EmptyLink color="#00539f">
          <span className={style.importantInfo}>
            <span className={style.infoIcon}>i</span>
            <span className={style.importantInfoText}>{t('questions', 'ec', 'tesco', 'importantInformation')}</span>
          </span>
        </EmptyLink>
      )}

      <EmptyLink color="#00539f">
        <span className={style.saveToList}>
          <img src={favouriteIcon} alt="" role="presentation" className={style.saveToListIcon} />
          {t('questions', 'ec', 'tesco', 'saveToList')}
        </span>
      </EmptyLink>
    </div>
  )

  const decorativeSections = DECORATIVE_SECTIONS.map(key => (
    <div className={`${style.section} ${style.stretch}`} key={key}>
      <EmptyLink color="#00539f">
        <span className={style.sectionHeader}>
          {t('questions', 'ec', 'tesco', key)}
          <span className={style.sectionChevron}>
            <i className="pi pi-chevron-down" />
          </span>
        </span>
      </EmptyLink>
    </div>
  ))

  const sections = (
    <div className={style.sections}>
      {hasAbout && (
        <div className={style.section}>
          <button
            type="button"
            className={style.sectionHeader}
            aria-expanded={aboutOpen}
            onClick={() => setAboutOpen(open => !open)}
          >
            {t('questions', 'ec', 'tesco', 'sectionAbout')}
            <span className={`${style.sectionChevron} ${aboutOpen ? style.sectionChevronOpen : ''}`}>
              <i className="pi pi-chevron-down" />
            </span>
          </button>
          {aboutOpen && (
            <div className={style.sectionBody}>
              {answer.description && (
                <div
                  className={style.description}
                  dangerouslySetInnerHTML={{ __html: unescapeTags(answer.description) }}
                />
              )}
              {answer.feature_bullets && (
                <div
                  className={style.description}
                  dangerouslySetInnerHTML={{ __html: unescapeTags(answer.feature_bullets) }}
                />
              )}
            </div>
          )}
        </div>
      )}
      {decorativeSections}
    </div>
  )

  const legal = (
    <div className={style.legal}>
      <h2 className={style.legalTitle}>{t('questions', 'ec', 'tesco', 'legalTitle')}</h2>
      {LEGAL_PARAGRAPHS.map(key => (
        <p key={key} className={style.legalText}>
          {t('questions', 'ec', 'tesco', key)}
        </p>
      ))}
      <p className={style.legalText}>{t('questions', 'ec', 'tesco', 'legalCopyright')}</p>
    </div>
  )

  const spinner = (
    <div className={viewport.mobile ? style.productMobile : style.product}>
      <Spinner />
    </div>
  )

  const carousel = featured.length > 0 && (
    <FeaturedCarousel
      schema={schema}
      response={response}
      products={featured}
      onSelect={index => openProduct(index, 'featured')}
      onAddToCart={onAddToCart}
    />
  )

  // The app's page: one column, the gallery swiping edge to edge, About as an open card, the
  // carousel before the decorative sections, and a buy bar over the tab bar once the buy row is gone.
  if (viewport.mobile) {
    const prices = (
      <div className={style.pricesMobile}>
        <span className={style.price} dangerouslySetInnerHTML={{ __html: price(answer.price) }} />
        {answer.per_count_price && (
          <span
            className={style.unitPrice}
            dangerouslySetInnerHTML={{ __html: unescapeTags(answer.per_count_price) }}
          />
        )}
      </div>
    )
    const addButton = (
      <AddToCartButton
        amount={response.answers[answer.index].v}
        limit={schema.max_qty_per_product}
        onAdd={qnt => onAddToCart(answer.index, qnt)}
      />
    )

    return (
      <AssetsGate urls={pageImages} loader={spinner}>
        <div className={style.productMobile} data-testid="SW-EC-Product">
          <Gallery key={answer.id} answer={answer} />
          <div className={style.info}>
            {answer.sponsored && <div className={style.sponsored}>{t('questions', 'ec', 'tesco', 'sponsored')}</div>}
            <h1 ref={setTitleElement} className={style.title} dangerouslySetInnerHTML={{ __html: answer.text }} />
            {hasRating && (
              <div className={style.ratingRow}>
                <Rating rating={answer.rating} />
                <span className={style.ratingText}>
                  {t<(rating: string) => string>('questions', 'ec', 'tesco', 'stars')(Number(answer.rating).toFixed(1))}
                  {answer.reviews != null && ` (${answer.reviews.toLocaleString(languageTag)})`}
                </span>
              </div>
            )}
            <div ref={setBuyRowElement} className={style.buyRow}>
              {prices}
              {addButton}
            </div>
            <EmptyLink color="#00539f">
              <span className={style.saveToList}>
                <img src={favouriteIcon} alt="" role="presentation" className={style.saveToListIcon} />
                {t('questions', 'ec', 'tesco', 'addToList')}
              </span>
            </EmptyLink>
            <VariantSelect schema={schema} answer={answer} onSelect={index => openProduct(index)} />
            <ClubcardPrice schema={schema} answer={answer} />
            <AldiPriceMatch answer={answer} />
            {answer.best_seller && (
              <EmptyLink color="#00539f">
                <span className={style.importantInfo}>
                  <span className={style.infoIcon}>i</span>
                  <span className={style.importantInfoText}>
                    {t('questions', 'ec', 'tesco', 'importantInformation')}
                  </span>
                </span>
              </EmptyLink>
            )}
          </div>

          {hasAbout && (
            <div className={style.aboutCard}>
              <h2 className={style.aboutTitle}>{t('questions', 'ec', 'tesco', 'sectionAbout')}</h2>
              {answer.description && (
                <div
                  className={style.description}
                  dangerouslySetInnerHTML={{ __html: unescapeTags(answer.description) }}
                />
              )}
              {answer.feature_bullets && (
                <div
                  className={style.description}
                  dangerouslySetInnerHTML={{ __html: unescapeTags(answer.feature_bullets) }}
                />
              )}
              <div className={style.readMoreSlot}>
                <EmptyLink color="#00539f">
                  <span className={style.readMore}>
                    {t('questions', 'ec', 'tesco', 'readMore')}
                    <i className="pi pi-chevron-right" />
                  </span>
                </EmptyLink>
              </div>
            </div>
          )}
          {carousel}
          <div className={style.sections}>{decorativeSections}</div>
          {legal}

          {buyRowPast && (
            <div className={style.stickyBuy}>
              {prices}
              {addButton}
            </div>
          )}
        </div>
      </AssetsGate>
    )
  }

  return (
    <AssetsGate urls={pageImages} loader={spinner}>
      <div className={style.product} data-testid="SW-EC-Product">
        {breadcrumbs}
        <div className={style.top}>
          <Gallery key={answer.id} answer={answer} />
          {info}
        </div>
        {sections}
        {legal}
        {carousel}
      </div>
    </AssetsGate>
  )
}

export default Product
